import React, { useCallback, useMemo } from 'react';
import { Box } from '@mui/material';

import { HandsontableCommon } from '../common/handsontable-common';
import { updateTableDataWithMetadata } from '../../hooks/use-confirm';

import { ConfirmSectionTitle } from './confirm-title';

import type { PlanTableData, PlanTableRow, RowsWithErrors } from '../common/handsontable.types';
import type { SearchOption } from '@/types/search';

interface UpdateSectionProps {
  tableData: PlanTableData;
  plantOptions: SearchOption[];
  witnessCompanyOptions: SearchOption[];
  rowsWithErrors?: RowsWithErrors;
  setRowsWithErrors?: (rowsWithErrors: RowsWithErrors) => void;
  onDataChange?: (data: PlanTableData) => void;
}

export const UpdateSection: React.FC<UpdateSectionProps> = ({
  tableData,
  plantOptions,
  witnessCompanyOptions,
  rowsWithErrors = [],
  setRowsWithErrors,
  onDataChange,
}) => {
  // ID column index (metadata column)
  const ID_COLUMN_INDEX = 23;

  // Split tableData into pairs: each pair is [originalRow, updatedRow]
  // Even indices (0, 2, 4...) are original rows, odd indices (1, 3, 5...) are updated rows
  const recordPairs = useMemo(() => {
    const pairs: PlanTableRow[][] = [];
    for (let i = 0; i < tableData.length; i += 2) {
      if (i + 1 < tableData.length) {
        pairs.push([tableData[i], tableData[i + 1]]);
      }
    }
    return pairs;
  }, [tableData]);

  // Map errors from pair-local indices to tableData indices
  // In tableData, pairs are stored as [original, updated, original, updated, ...]
  // So rowIndex in tableData = pairIndex * 2 + localRowIndex
  const mappedErrors = useMemo(() => {
    return rowsWithErrors.map((error) => ({
      ...error,
      rowIndex: error.rowIndex, // Already in tableData index format
    }));
  }, [rowsWithErrors]);

  // Create cells function for a specific pair
  // Local row indices: 0 = original, 1 = updated
  const createCellsFunction = useCallback(
    (originalRow: PlanTableRow, updatedRow: PlanTableRow) => {
      return (row: number, col: number, _prop: string | number) => {
        // Row 0 = original row -> readOnly and disabled
        if (row === 0) {
          return {
            readOnly: true,
            className: 'update-original-row',
          };
        }
        // Row 1 = updated row
        // Compare with original row to find changed cells
        const isDataColumn = col < ID_COLUMN_INDEX;
        if (!isDataColumn || !originalRow || !updatedRow) {
          return {};
        }

        // Normalize values for comparison (handle string/number/whitespace differences)
        const originalValue = originalRow[col];
        const updatedValue = updatedRow[col];
        const originalStr = originalValue != null ? String(originalValue).trim() : '';
        const updatedStr = updatedValue != null ? String(updatedValue).trim() : '';
        const hasChanged = originalStr !== updatedStr;

        // Apply yellow background for all updated rows, but red text only for changed cells
        return {
          className: hasChanged ? 'update-modified-cell' : 'update-unchanged-cell',
        };
      };
    },
    [ID_COLUMN_INDEX]
  );

  // Calculate count: each updated record has 2 rows (original + updated), so divide by 2
  const updateCount = Math.floor(tableData.length / 2);

  if (tableData.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 4 }}>
      <ConfirmSectionTitle title='内容更新' count={updateCount} />
      {recordPairs.map((pair, index) => {
        const [originalRow, updatedRow] = pair;
        const cells = createCellsFunction(originalRow, updatedRow);
        const showHeaders = index === 0; // Only first table has headers

        // Map errors for this pair: tableData index = index * 2 (original) or index * 2 + 1 (updated)
        // Only show errors for updated row (index * 2 + 1)
        const pairErrors = mappedErrors
          .filter((error) => {
            const pairStartIndex = index * 2;
            return error.rowIndex === pairStartIndex + 1; // Only updated row
          })
          .map((error) => ({
            ...error,
            rowIndex: 1, // Map to local row index (1 = updated row in pair)
          }));

        // Use ID from updated row as key, fallback to original row ID if needed
        // This avoids using array index as key (SonarQube requirement)
        const keyId =
          updatedRow?.[ID_COLUMN_INDEX] || originalRow?.[ID_COLUMN_INDEX] || `pair-${index}`;

        return (
          <Box key={keyId} sx={{ mb: index < recordPairs.length - 1 ? 2 : 0 }}>
            <HandsontableCommon
              tableData={pair}
              plantOptions={plantOptions}
              witnessCompanyOptions={witnessCompanyOptions}
              rowsWithErrors={pairErrors}
              setRowsWithErrors={(errors) => {
                if (setRowsWithErrors) {
                  // Map errors back to tableData indices
                  const mappedBackErrors = errors.map((error) => ({
                    ...error,
                    rowIndex: index * 2 + error.rowIndex, // Map back to tableData index (0->index*2, 1->index*2+1)
                  }));
                  // Merge with other pairs' errors (keep errors from other pairs)
                  const otherErrors = rowsWithErrors.filter(
                    (e) => Math.floor(e.rowIndex / 2) !== index
                  );
                  setRowsWithErrors([...otherErrors, ...mappedBackErrors]);
                }
              }}
              onDataChange={(data) => {
                if (onDataChange) {
                  const updatedTableData = updateTableDataWithMetadata(tableData, data, index);
                  onDataChange(updatedTableData);
                }
              }}
              className='handsontable-confirm-update'
              cells={cells}
              showHeaders={showHeaders}
            />
          </Box>
        );
      })}
    </Box>
  );
};
